import {
  createAsyncThunk,
  createSlice,
  appLogout,
  injectSlice,
  persistReducer,
  createAction,
} from '@bonanza-social/toolkit/state';
import { AsyncStorage } from '@bonanza-social/toolkit/storage';
import { DailyReward, DailyRewardsState } from './dailyRewards.types';
import DailyRewardsService from '../../services/DailyRewardsService/DailyRewardsService';

export const dailyRewardCollected = createAction<DailyReward>(
  'dailyRewards/rewardCollected',
);

const initialState: DailyRewardsState = {
  data: undefined,
  dataState: 'idle',
};

export const fetchDailyRewards = createAsyncThunk(
  'dailyRewards/fetch',
  async () => await DailyRewardsService.getDailyRewards(),
);

const dailyRewards = createSlice({
  name: 'dailyRewards',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(fetchDailyRewards.pending, (state) => {
      state.dataState =
        state.dataState === 'fulfilled' ? 'refreshing' : 'pending';
    });

    builder.addCase(fetchDailyRewards.rejected, (state) => {
      state.dataState = 'rejected';
    });

    builder.addCase(fetchDailyRewards.fulfilled, (state, { payload }) => {
      state.dataState = 'fulfilled';
      // TODO: Check with BE why sometimes the payload is empty
      if (payload.dailyRewards) {
        state.data = payload.dailyRewards;
      }
    });

    builder.addCase(appLogout, (state) => {
      Object.assign(state, initialState);
    });
  },
});

const persistConfig = {
  key: 'dailyRewards',
  storage: AsyncStorage,
  whitelist: ['data'],
};

const persistedReducer = persistReducer(persistConfig, dailyRewards.reducer);

export const withDailyRewards = injectSlice({
  reducerPath: 'dailyRewards',
  reducer: persistedReducer,
});

export const selectDailyGift = withDailyRewards.selector((state) => {
  const dailyGift = state.dailyRewards.data?.find(
    (item) => item.type === 'DailyGift',
  );
  return dailyGift ? (dailyGift as DailyReward) : undefined;
});

export const selectDailyScratcher = withDailyRewards.selector((state) => {
  const dailyScratcher = state.dailyRewards.data?.find(
    (item) => item.type === 'DailyScratcher',
  );
  return dailyScratcher ? (dailyScratcher as DailyReward) : undefined;
});

export const selectDailyWheel = withDailyRewards.selector((state) => {
  return state.dailyRewards.data?.find((item) => item.type === 'DailyWheel');
});

export const selectDailyGiftStatus = withDailyRewards.selector(
  (state) =>
    state.dailyRewards.data?.find((item) => item.type === 'DailyGift')?.status,
);

export const selectDailyScratcherStatus = withDailyRewards.selector(
  (state) =>
    state.dailyRewards.data?.find((item) => item.type === 'DailyScratcher')
      ?.status,
);

export const selectDailyScratcherPlayerRewardId = withDailyRewards.selector(
  (state) => {
    const dailyScratcher = state.dailyRewards.data?.find(
      (item) => item.type === 'DailyScratcher',
    );

    if (dailyScratcher && dailyScratcher.status !== 'Locked') {
      return dailyScratcher.playerRewardId;
    }
  },
);

export const selectIsAnyDailyRewardNotAvailable = withDailyRewards.selector(
  (state) =>
    state.dailyRewards.data?.some((item) => item.status === 'NotAvailable'),
);

export const selectIsAnyDailyRewardWithPositiveRewardAmount =
  withDailyRewards.selector((state) =>
    state.dailyRewards.data?.some(
      (item) =>
        item.status !== 'Locked' && item.rewardAmount && item.rewardAmount > 0,
    ),
  );

export const selectDailyWheelStatus = withDailyRewards.selector(
  (state) =>
    state.dailyRewards.data?.find((item) => item.type === 'DailyWheel')?.status,
);

export const selectDailyWheelAvailableAtStatus = withDailyRewards.selector(
  (state) => {
    const dailyWheel = state.dailyRewards.data?.find(
      (item) => item.type === 'DailyWheel',
    );

    if (dailyWheel?.status === 'Locked') {
      return dailyWheel.availableAtLoyaltyStatus;
    }
  },
);

export const selectDailyWheelIsMetaGame = withDailyRewards.selector((state) => {
  const dailyWheel = state.dailyRewards.data?.find(
    (item) => item.type === 'DailyWheel',
  );

  return Boolean(
    dailyWheel && dailyWheel.status !== 'Locked' && dailyWheel.isExternalReward,
  );
});

export const selectDailyRewardsDataState = withDailyRewards.selector(
  (state) => state.dailyRewards.dataState,
);

declare global {
  interface LazyLoadedSlices {
    dailyRewards: ReturnType<typeof persistedReducer>;
  }
}
